// src/pages/produtos/produtos/ProdutoCad.js
import { useState, useEffect } from 'react';

import { Produto } from '../../../models/produtos/Produto';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import Input from '../../../components/input/Input';
import ProdutoSearch from './ProdutoSearch';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import ImpostosTab from './ImpostosTab';
import DeliveryTab from './DeliveryTab';
import AdicionaisTab from './AdicionaisTab';
import GeralTab from './GeralTab';
import { useProdutoConjunto } from '../../../contexts/produtos/combos/ProdutoConjuntoContext';
import ConjuntoTab from './ConjuntoTab';
import VariacaoTab from './VariacaoTab';
import { useAdicional } from '../../../contexts/produtos/AdicionalContext';
import ApresentacaoCad from '../combos/ApresentacaoCad';
import ApresentacaoTab from './ApresentacaoTab';
import { useConfigModulo } from '../../../contexts/administrativo/ConfigModuloContext';
import ProdutoFornecTab from './ProdutoFornecTab';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import { formatNumeric } from '../../../utils/numberUtils';
import ModalTabs from '../../../components/modalForm/ModalTabs';
import ModalActions, { ModalActionButton } from '../../../components/modalForm/ModalActions';

const prepararProdutoCadastro = (produtoSelecionado) => produtoSelecionado
    ? { ...produtoSelecionado, preco: formatNumeric(produtoSelecionado.preco) }
    : new Produto();

const ProdutoCad = ({ show, onHide, initialProduto, onSaveProduto }) => {
    const { produtos, updateProduto, addProduto, deleteProduto } = useProduto();  
    const { adicionaisProduto, setAdicionaisProduto } = useAdicional();   
    const { fetchProdutoConjuntosDetalhado } = useProdutoConjunto();    
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();

    const [produto, setProduto] = useState(() => prepararProdutoCadastro(initialProduto));
    const [isSaving, setIsSaving] = useState(false);

    const [disableId, setDisableId] = useState(false);
    const [disableCampos, setDisableCampos] = useState(false);
    const [disableBtnNovo, setDisableBtnNovo] = useState(true);
    const [disableBtnExcluir, setDisableBtnExcluir] = useState(true);
    const [titulo, setTitulo] = useState('Novo Produto');
    const [activeTab, setActiveTab] = useState('geral');
    const { configModuloSel } = useConfigModulo();

    const [showCadApresentacao, setShowCadApresentacao] = useState(false);

    // Search Modal    
    const [showSearchProduto, setShowSearchProduto] = useState(false);    

    const handleDuplicar = (e) => {
        e.preventDefault();
        if (!produto?.id) return;

        const novoProduto = {
            ...produto,
            id: undefined,
            descricao: `${produto.descricao} (Cópia)`,
            imagem: null,
            imagemId: null,
            adicionais: []
        };

        setProduto(novoProduto);
        setAdicionaisProduto([]);
        setDisableId(false);
        setDisableCampos(false);
        setDisableBtnNovo(true);
        setDisableBtnExcluir(true);
        setTitulo('Duplicar Produto');
    };

    useEffect(() => {
        aplicarProdutoSelecionado(initialProduto);
    }, [initialProduto]);    

    const handleNovo = () => {
        aplicarProdutoSelecionado(null);
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione um produto para editar!");            
            return;
        }

        setDisableId(true);
        setDisableCampos(false);
        setDisableBtnNovo(false);
        setDisableBtnExcluir(false);        
        setTitulo('Editar Produto');
    };

    const aplicarProdutoSelecionado = (produtoSelecionado) => {
        setProduto(prepararProdutoCadastro(produtoSelecionado));
        setAdicionaisProduto([...(produtoSelecionado?.adicionais || [])]);
        setShowSearchProduto(false);
        setIsSaving(false);

        if (produtoSelecionado?.id) {
            handleEditar(produtoSelecionado.id);
        } else {
            setDisableId(false);
            setDisableCampos(false);
            setDisableBtnNovo(true);
            setDisableBtnExcluir(true);
            setTitulo('Novo Produto');
        }
    };

    const handleDelete = async () => {
        if (!produto?.id) {
            showMessage("Selecione um produto para excluir!");            
        } else {
            const resposta = await showYesNo("Deseja excluir o produto selecionado?");
            if (resposta) {
                await deleteProduto(produto.id);     
                handleNovo();
            }
        }
    };    

    const handleSave = async () => {
        if (isSaving) return; // Previne cliques múltiplos

        setIsSaving(true);
        try {
            if (!produto.descricao) {
                showMessage("Informe uma descrição!");                
                return;
            }
            if (!produto.grupo?.id) {
                showMessage("Grupo não selecionado!");                
                return;
            }
            if (!produto.marca?.id) {
                showMessage("Marca não selecionada!");                
                return;
            }
            if (!produto.unMedida?.sigla) {
                showMessage("Unidade de Medida não selecionada!");                
                return;
            }

            const payload = {
                ...produto,
                grupoId: produto.grupo.id,
                subGrupoId: produto.subGrupo?.id || null,
                marcaId: produto.marca.id,
                unTrib: produto.unMedida.sigla,
                destaqueProdutoId: produto.destaqueProduto?.id || produto.destaqueProdutoId || null,
                adicionais: adicionaisProduto ? adicionaisProduto.map(adicional => ({
                    id: adicional.id,
                    produtoId: adicional.produtoId,
                    descricao: adicional.descricao,
                    preco: adicional.preco,
                    obrigatorio: adicional.obrigatorio || false
                })) : null
            };

            delete payload.grupo;
            delete payload.subGrupo;
            delete payload.marca;
            delete payload.unMedida;

            delete payload.destaqueProduto;

            const produtoSalvo = !produto.id
                ? await addProduto(payload)
                : await updateProduto(payload);

            if (!produtoSalvo) {
                return;
            }

            if (onSaveProduto) {
                onSaveProduto(produtoSalvo);
            }

            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
            showMessage("Erro ao salvar produto!");
        } finally {
            setIsSaving(false);
        }
    };

    const handleExit = () => {
        const produtoSelecionado = produtos.find(p => p.id.toString() === produto?.id?.toString());
        aplicarProdutoSelecionado(produtoSelecionado);
    };

    const handleSelectProduto = (produtoSelecionado) => {
        aplicarProdutoSelecionado(produtoSelecionado);
    };

    const handleTabChange = (tab) => (e) => {
        e.preventDefault();
        setActiveTab(tab);
    };

    //Testar
    useEffect(() => {
        fetchProdutoConjuntosDetalhado(produto?.id || 0);
    }, [produto?.id]);

    if (!show) return null;

    return (
        <ModalContainer show={show} largura='650px' zIndex={1060} variant="cadastro" onHide={onHide}>
            <ModalHeader title={titulo} onHide={onHide} />
            <ModalBody>
                <form>
                    <div className="input-group">
                        <Input
                            label="Código"
                            value={produto.id || ''}
                            onChange={(e) => setProduto({ ...produto, id: e.target.value })}
                            onBlur={handleExit}
                            disabled={disableId}
                            btnBuscar={() => setShowSearchProduto(true)}
                            size={80}                                                                                                          
                        />    
                        <Input
                            label="Descrição"
                            value={produto?.descricao || ''}   
                            onChange={(e) => setProduto({ ...produto, descricao: e.target.value })}                                     
                            size={450}
                            disabled={disableCampos}   
                            autoFocus={true}                               
                        />                                                             
                    </div>

                    <ModalTabs >
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={handleTabChange('geral')}>Geral
                            </button>
                        </li>
                        {(configModuloSel?.emiteNFCe || configModuloSel?.emiteNFe) && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'impostos' ? 'active' : ''}`}
                                onClick={handleTabChange('impostos')}>Impostos
                            </button>
                        </li>
                        )}
                        {configModuloSel?.controleEstoque && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'fornecedores' ? 'active' : ''}`}
                                onClick={handleTabChange('fornecedores')}>Fornecedores
                            </button>
                        </li>
                        )}

                        {configModuloSel?.moduloPedido && (
                        <>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'delivery' ? 'active' : ''}`}
                                onClick={handleTabChange('delivery')}>Delivery
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'variacoes' ? 'active' : ''}`}
                            onClick={handleTabChange('variacoes')}>Variações
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'adicionais' ? 'active' : ''}`}
                                onClick={handleTabChange('adicionais')}>Adicionais
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'conjuntos' ? 'active' : ''}`}
                                onClick={handleTabChange('conjuntos')}>Conjuntos
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'apresentacao' ? 'active' : ''}`}
                                onClick={handleTabChange('apresentacao')}>Apresentação 
                            </button>
                        </li>
                        </>)}
                    </ModalTabs>

                    <ModalTabs.Panel >
                        {activeTab === 'geral' && (
                            <GeralTab
                                produto={produto}
                                setProduto={setProduto}
                                disableCampos={disableCampos}
                            />
                        )}
                        {activeTab === 'impostos' && (
                            <ImpostosTab
                                produto={produto}
                                setProduto={setProduto}
                                disableCampos={disableCampos}
                            />
                        )}
                        {activeTab === 'fornecedores' && (
                            <ProdutoFornecTab
                                produto={produto}
                                setProduto={setProduto}
                                disableCampos={disableCampos}
                            />
                        )}

                        {activeTab === 'delivery' && (
                            <DeliveryTab
                                produto={produto}
                                setProduto={setProduto}
                                disableCampos={disableCampos}
                            />
                        )}
                        {activeTab === 'adicionais' && (
                            <AdicionaisTab
                                produto={produto}
                                setProduto={setProduto}
                                disableId={disableId}
                                disableCampos={disableCampos}
                            />
                        )}
                        {activeTab === 'variacoes' && (
                            <VariacaoTab produto={produto} setProduto={setProduto} disableCampos={!disableId} />
                        )}
                        {activeTab === 'conjuntos' && (
                            <ConjuntoTab
                                produto={produto}
                                // setProduto={setProduto}
                                disableCampos={!disableId}
                            />
                        )}  
                        {activeTab === 'apresentacao' && (
                            // <button type='button' onClick={()=>setShowCadApresentacao(true)}>
                            //     Cad Apresentacao
                            // </button>
                            <ApresentacaoTab
                                produto={produto}
                                // setProduto={setProduto}
                                disableCampos={!disableId}
                            />
                        )}
                    </ModalTabs.Panel>
                </form>
            </ModalBody>
            <ModalFooter>
                <ModalActionButton
                    className="btn btn-secondary"
                    style={{ paddingTop: '5px', paddingBottom: '5px' }}
                    onClick={handleDuplicar}
                    hidden={!disableId || isSaving}
                    action="duplicate">
                    Duplicar Produto
                </ModalActionButton>
                <ModalActions >
                    <ModalActionButton
                        className="btn btn-primary"
                        style={{ paddingTop: '5px', paddingBottom: '5px' }}
                        onClick={handleSave}
                        disabled={isSaving}
                     action="save">
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </ModalActionButton>
                    <ModalActionButton
                        className="btn btn-danger"
                        style={{ paddingTop: '5px', paddingBottom: '5px' }}
                        onClick={handleDelete}
                        disabled={disableBtnExcluir || isSaving}
                     action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton
                        className="btn btn-secondary"
                        style={{ paddingTop: '5px', paddingBottom: '5px' }}
                        onClick={handleNovo}
                        disabled={disableBtnNovo || isSaving}
                     action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>
            <ProdutoSearch
                show={showSearchProduto}
                onHide={() => setShowSearchProduto(false)}
                onSelectProduto={handleSelectProduto}
            /> 

            <ApresentacaoCad
                show={showCadApresentacao}
                onHide={() => setShowCadApresentacao(false)}
            ></ApresentacaoCad>                                       
        </ModalContainer>
    );
};

export default ProdutoCad;
